import React, { useEffect, useState } from 'react';
import Header from '../Shared/Header';
import Sidebar from '../Shared/Sidebar';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config';
import './SubscriptionPage.css';

const SubscriptionPage = () => {
    const { t, i18n } = useTranslation(['subscriptions', 'common']);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        const fetchHistory = async () => {
            if (!userId) {
                setError("User not logged in");
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`${API_BASE_URL}get_user_payments.php?user_id=${userId}`, { credentials: 'include' });
                const result = await res.json();
                if (result.status === 'success') {
                    setData(result);
                } else {
                    setError(result.message);
                }
            } catch (err) {
                setError(t('subscriptions:subscriptions.errors.fetchFailed', 'Failed to fetch history'));
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [userId]);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString(i18n.language === 'en' ? 'en-IN' : i18n.language, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="dashboard-wrapper">
                <div className="dashboard-container">
                    <Sidebar active="subscription" />
                    <main className="main-content-area">
                        <Header title={`💳 ${t('subscriptions:subscriptions.title', 'My Subscriptions')}`} subtitle={t('subscriptions:subscriptions.subtitle', 'Manage your plans and billing')} hidePill={true} />
                        <div className="container" style={{ textAlign: 'center', marginTop: '4rem' }}>
                            <div className="spinner-center"></div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="subscription" />

                <main className="main-content-area">
                    <Header title={`💳 ${t('subscriptions:subscriptions.title', 'My Subscriptions')}`} subtitle={t('subscriptions:subscriptions.subtitle', 'Manage your plans and billing')} hidePill={true} />
                    <div className="dashboard-scroll-container">
                        <main className="container subscription-content">

                            {/* Current Status Card */}
                            <div className="status-overview-row">
                                <div className="subscription-status-card frosted-glass border-glow-blue">
                                    <div className="card-icon">🎯</div>
                                    <div className="card-info">
                                        <h3>{t('subscriptions:subscriptions.currentPlan', 'Current Plan')}</h3>
                                        <p className="plan-name-badge">
                                            {data?.subscription?.role === 'premium' ? t('subscriptions:subscriptions.premiumPlan', 'Premium Plan') : t('subscriptions:subscriptions.freeLearner', 'Free Learner')}
                                        </p>
                                    </div>
                                </div>

                                <div className="subscription-status-card frosted-glass border-glow-purple">
                                    <div className="card-icon">📚</div>
                                    <div className="card-info">
                                        <h3>{t('subscriptions:subscriptions.chapterAccess', 'Chapter Access')}</h3>
                                        <p className="chapter-count">
                                            {t('subscriptions:subscriptions.premiumChapters', { count: data?.subscription?.chapter_count || 0, defaultValue: `${data?.subscription?.chapter_count || 0} Premium Chapters` })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Purchase History Table */}
                            <div className="history-section frosted-glass border-glow">
                                <div className="history-header">
                                    <h2>{t('subscriptions:subscriptions.purchaseHistory', 'Purchase History')}</h2>
                                </div>
                                {error ? (
                                    <div className="error-message">{error}</div>
                                ) : data?.payments?.length === 0 ? (
                                    <div className="empty-history">
                                        <p>{t('subscriptions:subscriptions.noTransactions', 'No transactions found.')}</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="purchase-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('subscriptions:subscriptions.table.date', 'Date')}</th>
                                                    <th>{t('subscriptions:subscriptions.table.orderId', 'Order ID')}</th>
                                                    <th>{t('subscriptions:subscriptions.table.plan', 'Plan')}</th>
                                                    <th>{t('subscriptions:subscriptions.table.amount', 'Amount')}</th>
                                                    <th>{t('subscriptions:subscriptions.table.status', 'Status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.payments.map(payment => (
                                                    <tr key={payment.id}>
                                                        <td className="date-cell">{formatDate(payment.created_at)}</td>
                                                        <td className="order-id-cell">{payment.order_id}</td>
                                                        <td className="plan-cell">{payment.plan_name}</td>
                                                        <td className="amount-cell">₹{payment.amount}</td>
                                                        <td className="status-cell">
                                                            <span className={`status-badge ${payment.status.toLowerCase()}`}>
                                                                {payment.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default SubscriptionPage;
